import { CommonModule } from '@angular/common'
import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from '@angular/core'
import { FormsModule, UntypedFormControl, Validators } from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MatCheckboxModule } from '@angular/material/checkbox'
import { MatIconModule } from '@angular/material/icon'
import { MatMenuModule } from '@angular/material/menu'
import { MatSlideToggleModule } from '@angular/material/slide-toggle'
import { MatToolbarModule } from '@angular/material/toolbar'
import { WorkspaceRoleService } from '@app/modules/auth/services/workspace-role.service'
import { InvitationComponent } from '@app/modules/invite/components/invitation/invitation.component'
import { InvitationInfo, buildInvitationInfo } from '@app/modules/invite/models/invitation-info.interface'
import { PersonaTypes } from '@app/modules/personas/enums/persona-types.enum'
import { BetaFeaturesService } from '@app/services/beta-features.service'
import { ToastrService } from '@app/services/toastr.service'
import { ModalComponent } from '@app/v2/shared/components/modal/modal.component'
import { ModalService } from '@app/v2/shared/services/modal.service'
import { AuthService } from '@auth/services/auth.service'
import { RegistrationsDataSourceCollection } from '@database/models/registrations.datasource-collection'
import { SavedFilter } from '@database/models/saved-filter.interface'
import { AISearchResponse, DatabaseService } from '@database/services/database.service'
import { environment } from '@environments/environment'
import { ServerRepresentedEvent } from '@events/models/event.model'
import { BenchmarkService } from '@events/services/benchmark.service'
import { EventService } from '@events/services/event.service'
import { NbButtonModule, NbCardModule, NbContextMenuModule, NbDialogService, NbFormFieldModule, NbIconModule, NbInputModule, NbMenuItem, NbMenuService } from '@nebular/theme'
import { NgxTippyModule } from 'ngx-tippy-wrapper'
import { Subject } from 'rxjs'
import { debounceTime, distinctUntilChanged, filter, finalize, first, map, takeUntil } from 'rxjs/operators'
import { InviteService } from '../../../invite/services/invite.service'
import { ApprovalStatus } from '../../models/registration-status.enum'
import { RegistrationsDataSource } from '../../models/registrations.datasource'
import { AiSearchModalComponent } from '../ai-search-modal/ai-search-modal.component'
import { ImportRegistrationsComponent } from '../import-registrations/import-registrations.component'
import { InvitationJobsHistoryComponent } from '../invitation-jobs-history/invitation-jobs-history.component'
import { ResendConfirmationFlowComponent } from '../resend-confirmation-flow/resend-confirmation-flow.component'
import { RevisionRequestModalComponent } from '../revision-request-modal/revision-request-modal.component'
import { SavedFiltersDropdownComponent } from '../saved-filters-dropdown/saved-filters-dropdown.component'
import { SettleRegistrantsComponent } from '../settle-registrants/settle-registrants.component'
/**
 * Represents the Database View header, controls bulk options, settle, invite, imports and exports dialog
 */
@Component({
  selector: 'ngx-events-data-header',
  standalone: true,
  templateUrl: './data-header.component.html',
  styleUrls: ['./data-header.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    NbCardModule,
    NbButtonModule,
    MatSlideToggleModule,
    NbFormFieldModule,
    NgxTippyModule,
    MatIconModule,
    NbContextMenuModule,
    MatToolbarModule,
    NbInputModule,
    MatMenuModule,
    MatCheckboxModule,
    MatButtonModule,
    NbIconModule,
    SavedFiltersDropdownComponent,
    InvitationJobsHistoryComponent,
    ModalComponent,
    InvitationComponent
  ]
})
export class DataHeaderComponent implements OnInit, OnDestroy {
  /** Subject that stops all hot observables */
  private _destroy$ = new Subject()

  /**
   * Whether we are in archive mode
   */
  archived: any

  /**
   * Exporting to benchmark user email
   */
  benchmarkEmail: string
  /**
   * Exporting to benchmark user password
   */
  benchmarkPwd: string

  /**
   * Export menu options
   */
  exports: Array<{ title: string; data: { option: number }; icon: { icon: string; pack: string } }> = []

  /**
   * Filter options
   */
  statusOptions: NbMenuItem[] = [
    { title: 'Attended / Missed', data: { option: 'attended' }, selected: false },
    { title: 'Approved', data: { option: 'approved' }, selected: false },
    { title: 'Declined', data: { option: 'declined' }, selected: false },
    { title: 'Pending review', data: { option: 'pending_review' }, selected: false },
    { title: 'Awaiting revision', data: { option: 'awaiting_revision' }, selected: false },
    { title: 'Updated', data: { option: 'updated' }, selected: false },
    { title: 'Pending Payment', data: { option: 'pending_payment' }, selected: false },
    { title: 'Flagged', data: { option: 'flagged' }, selected: false },
    { title: 'Refunded', data: { option: 'refunded' }, selected: false },
    { title: 'Clear filter', data: { option: 'clear' }, selected: false }
  ]

  /**
   * Visability options
   */
  visabilityOptions: Array<
    Array<{
      title: string
      data: {
        option: string
        checked: boolean
        visable: boolean
      }
      icon: any
    }>
  > = []
  /**
   * Benchmark export show login or exporting
   */
  show = false
  /**
   * Benchmark export successful export status
   */
  showSuccess = false
  /**
   * Returns the currently selected source from the registrations data sources collection.
   * This includes the registrations data source itself along with its associated title, weight, and slug.
   */
  get selectedSource(): {
    registrationsDataSource: RegistrationsDataSource
    title: string
    weight: string
    slug: string
  } {
    return this.registrationsDataSourceCollection.registrationsDataSources[this.registrationsDataSourceCollection.selectedIndex]
  }
  @ViewChild('status', { static: false }) statusElement: ElementRef
  @ViewChild('savedFiltersDropdownRef') savedFiltersDropdown: any

  readonly settleToInviteModalId = 'settle-to-invite-modal'
  readonly inviteModalId = 'database-invite-to-event-modal'

  /** Pending invitation payload while the invite modal is open. */
  invitationInfo: InvitationInfo | null = null
  showInviteModal = false
  /** Prevents overlapping unsettled-count requests from rapid invite clicks. */
  private _inviteUnsettledCheckInFlight = false

  /** On destroy implementation */
  ngOnDestroy() {
    this._destroy$.next(null)
    this._destroy$.complete()

    // Clear the interval when component is destroyed
    if (this.selectedIndexChangeInterval) {
      clearInterval(this.selectedIndexChangeInterval)
    }
  }

  /**
   * Import export label
   */
  importExportLabel = 'IMPORT'

  importExportMenu: NbMenuItem[] = []
  /**
   * Hides the option to export to benchmark
   */
  hideBenchmark = true
  /**
   * Form control for the uploaded file
   */
  file: UntypedFormControl
  /**
   * Form control for confirmation emails
   */
  confirmationEmail: UntypedFormControl
  /**
   * Dialog reference
   */
  @ViewChild('dialog') dialog: TemplateRef<any>
  /**
   * Benchmark dialog refernce
   */
  @ViewChild('benchmarkConnect') benchmarkConnect: TemplateRef<any>
  /**
   * Import dialog refernce
   */
  @ViewChild('importRef') importTemplateRef: TemplateRef<any>
  /**
   * Event model
   */
  @Input()
  currentEvent: ServerRepresentedEvent

  /**
   * Export progress status
   */
  @Input()
  progressStatus: string

  /**
   * Export progress percentage
   */
  @Input()
  progressPercent: number

  /**
   * Whether something is selected in the database view
   */
  @Input()
  selectionExists: boolean

  /**
   * Current registration datasource collection
   */
  @Input()
  registrationsDataSourceCollection: RegistrationsDataSourceCollection

  /**
   * Search term change event emitter
   */
  @Output()
  searchTermChanged: EventEmitter<string> = new EventEmitter<string>()
  /**
   * Filter with status
   */
  @Output()
  filterStatusChanged: EventEmitter<{ status: string; flagged: string; refunded?: boolean; attended?: 'yes' | 'no' }> = new EventEmitter<{ status: string; flagged: string }>()
  /**
   * Auto refresh change event emitter
   */
  @Output()
  autoRefreshChanged: EventEmitter<boolean> = new EventEmitter<boolean>()
  /**
   * Settle complete event emitter
   */
  @Output()
  settleComplete: EventEmitter<any> = new EventEmitter()
  /**
   * Serch term change subject
   */
  searchTermChangedSubject: Subject<string> = new Subject<string>()
  /**
   * Current search term
   */
  private _searchTerm = ''
  /**
   * Autorefresh
   */
  autoRefresh = false
  /**
   * Export menu open
   */
  menuIsOpen = false
  /**
   * confirmation number for delete
   */
  confirmationNumber: string
  /**
   * confirmation text for delete
   */
  confirmationText = ''
  /**
   *  To determine if the event has started
   */
  isEventStarted = false

  /**
   * Unsettled count
   */
  unsettledCount = 0

  /**
   * Track if status filter is active (not default)
   */
  public isFilterSelected = false

  /**
   * Track if status menu is open
   */
  public isStatusMenuOpen = false

  /**
   * Track the currently selected status filter
   */
  private _selectedStatusOption: string | null = null

  /**
   * Active column filters for display in search bar
   */
  @Input()
  activeFilters: { field: string; label: string; values: string[]; inverted?: boolean }[] = []

  /**
   * Signal to clear the search term (used when switching personas)
   */
  @Input()
  set clearSearch(value: boolean) {
    if (value) {
      this._searchTerm = ''
      this.searchTermChangedSubject.next('')
    }
  }

  /**
   * Signal to set the search term without triggering change event (used when restoring filter state)
   */
  @Input()
  set searchTerm(value: string | null) {
    if (value !== null && value !== undefined) {
      this._searchTerm = value
      // Don't emit change event - just update the UI
    }
  }

  /**
   * Getter for search term (used in template)
   */
  get searchTerm(): string {
    return this._searchTerm
  }

  /**
   * Signal to clear filter UI state (used when switching personas)
   */
  @Input()
  set clearFilterState(value: boolean) {
    if (value) {
      this.isFilterSelected = false
      this._selectedStatusOption = null
      this._updateStatusOptionsSelected()
    }
  }

  /**
   * Saved filters for the current persona
   */
  @Input()
  savedFilters: SavedFilter[] = []

  /**
   * Event emitter for saving a filter
   */
  @Output()
  saveFilterRequested: EventEmitter<string> = new EventEmitter<string>()

  /**
   * Event emitter for loading a saved filter
   */
  @Output()
  loadFilterRequested: EventEmitter<SavedFilter> = new EventEmitter<SavedFilter>()

  /**
   * Event emitter for deleting a saved filter
   */
  @Output()
  deleteFilterRequested: EventEmitter<string> = new EventEmitter<string>()

  /**
   * Event emitter for removing a filter
   */
  @Output()
  filterRemoved: EventEmitter<string> = new EventEmitter<string>()

  /**
   * Event emitter for clearing all filters
   */
  @Output()
  allFiltersCleared: EventEmitter<void> = new EventEmitter<void>()

  /**
   * Event emitter for AI-generated filters
   */
  @Output()
  aiFiltersGenerated: EventEmitter<AISearchResponse['filters']> = new EventEmitter<AISearchResponse['filters']>()

  /**
   * Creates the component and injects it's dependencies
   * @param eventService EventService
   * @param toastrService ToastrService
   * @param dialogService NbDialogService
   * @param nbMenuService NbMenuService
   * @param auth AuthService
   * @param benchmarkService BenchmarkService
   * @param databaseService DatabaseService
   * @param workspaceRole WorkspaceRoleService
   */
  constructor(
    private eventService: EventService,
    public toastrService: ToastrService,
    private dialogService: NbDialogService,
    private nbMenuService: NbMenuService,
    public auth: AuthService,
    private benchmarkService: BenchmarkService,
    private databaseService: DatabaseService,
    public workspaceRole: WorkspaceRoleService,
    private _inviteService: InviteService,
    private betaFeaturesService: BetaFeaturesService,
    private _modalService: ModalService,
    private _cdr: ChangeDetectorRef
  ) {}

  /**
   * Checks if the Database AI Search beta feature is enabled
   */
  get isAISearchEnabled(): boolean {
    return this.betaFeaturesService.isFeatureEnabled('database_ai_search')
  }

  /**
   * Initializes the component
   */
  ngOnInit() {
    const parsedStartDate = Date.parse(this.currentEvent.registration_start_date)
    this.isEventStarted = isNaN(parsedStartDate) ? Date.parse(this.currentEvent.registration_start_date.slice(0, 10)) <= Date.now() : parsedStartDate <= Date.now()

    const excludedColumns = ['#select', '#flag', '#barcode', '#edit', '#attend', '#filler']
    this.visabilityOptions = this.registrationsDataSourceCollection.registrationsDataSources.map((persona) => {
      return persona.registrationsDataSource.displayColumns.map((column) => {
        let title = persona.registrationsDataSource.columnDefinitions.find((col) => col.field === column)?.header
        if (!title) {
          switch (column) {
            case '#approve':
              title = 'Approval status'
              break
            case '#approved_by':
              title = 'Approved / Declined By'
              break
            case '#email':
              title = 'Confirmation Email'
              break
            case '#created_at':
              title = 'Created at'
              break
            default:
              title = column
                .replace('_', ' ')
                .replace(/\b\w/g, (l) => l.toUpperCase())
                .replace('#', '')
          }
        }
        if (excludedColumns.includes(column)) return { title, data: { option: column, checked: true, visable: false }, icon: {} }
        return { title, data: { option: column, checked: true, visable: true }, icon: {} }
      })
    })

    // Get the current selected datasource title
    this.setupImportExportMenu()

    // Store current selected index for change detection
    this.lastSelectedIndex = this.registrationsDataSourceCollection.selectedIndex

    // Check for selected index changes periodically
    this.selectedIndexChangeInterval = setInterval(() => {
      if (this.lastSelectedIndex !== this.registrationsDataSourceCollection.selectedIndex) {
        this.lastSelectedIndex = this.registrationsDataSourceCollection.selectedIndex
        this.setupImportExportMenu()
      }
    }, 500)

    this.archived = this.currentEvent.is_archived
    this.searchTermChangedSubject.pipe(debounceTime(500), distinctUntilChanged(), takeUntil(this._destroy$)).subscribe((term) => {
      this.searchTerm = term
      this.searchTermChanged.emit(term)
    })

    this.file = new UntypedFormControl('', [Validators.required])
    this.confirmationEmail = new UntypedFormControl(false)
    this.hideBenchmark = !this.benchmarkService.getWithExpiry('benchmarkToken') ? false : true

    this.nbMenuService
      .onItemClick()
      .pipe(
        filter(({ tag }) => tag === 'my-eports-menu' || tag === 'filter-option'),
        map(({ item: { data } }) => data.option)
      )
      .pipe(takeUntil(this._destroy$))
      .subscribe((title) => {
        switch (title) {
          case 1:
            this.openImportDialog()
            break
          case 2:
            this.openExportDialog(this.dialog)
            break
          case 3:
            this.openDialog(this.benchmarkConnect)
            if (!this.benchmarkService.getWithExpiry('benchmarkToken')) {
              this.show = false
              this.showSuccess = false
            } else {
              this.showSuccess = true
              this.exportBm()
            }
            break
          case 'approved':
            this.filterStatusChanged.emit({ status: 'yes', flagged: '' })
            this._applyFilter(title, 'Approved')
            break
          case 'declined':
            this.filterStatusChanged.emit({ status: 'no', flagged: '' })
            this._applyFilter(title, 'Declined')
            break
          case 'pending_review':
            this.filterStatusChanged.emit({ status: 'pending', flagged: '' })
            this._applyFilter(title, 'Pending review')
            break
          case 'awaiting_revision':
            this.filterStatusChanged.emit({ status: 'request_revision', flagged: '' })
            this._applyFilter(title, 'Awaiting revision')
            break
          case 'updated':
            this.filterStatusChanged.emit({ status: 'updated', flagged: '' })
            this._applyFilter(title, 'Updated')
            break
          case 'pending_payment':
            this.filterStatusChanged.emit({ status: 'pending_payment', flagged: '' })
            this._applyFilter(title, 'Pending payment')
            break
          case 'flagged':
            this.filterStatusChanged.emit({ status: '', flagged: 'yes' })
            this._applyFilter(title, 'Flagged')
            break
          case 'refunded':
            this.filterStatusChanged.emit({ status: '', flagged: '', refunded: true })
            this._applyFilter(title, 'Refunded')
            break
          case 'attended':
            this.filterStatusChanged.emit({ status: '', flagged: '', attended: 'yes' })
            this._applyFilter(title, 'Attended / Missed')
            break
          case 'clear':
            this.filterStatusChanged.emit({ status: '', flagged: '' })
            this._applyFilter(title, 'Status')
            break
          default:
            break
        }
      })
  }
  /**
   * Applies the filter to the status options
   * @param filterName
   * @param filterTitle
   */
  private _applyFilter(
    filterName: 'approved' | 'declined' | 'pending_review' | 'awaiting_revision' | 'updated' | 'pending_payment' | 'flagged' | 'refunded' | 'attended' | 'clear',
    filterTitle: string
  ): void {
    if (this.statusElement?.nativeElement) this.statusElement.nativeElement.innerHTML = filterTitle
    this.isFilterSelected = filterName === 'clear' ? null : true
    this.isStatusMenuOpen = false
    this._selectedStatusOption = filterName === 'clear' ? null : filterName
    this._updateStatusOptionsSelected()
  }

  get canImportOrExport(): boolean {
    return this.workspaceRole.canImportToEvent() || this.workspaceRole.canExportDatabase() || this.auth.isAdmin
  }
  // Member-persona import is platform-admin only. Other personas keep the import permission.
  setupImportExportMenu(): void {
    if (!this.canImportOrExport) return
    this.importExportMenu = []
    const hasRegistrants = this.registrationsDataSourceCollection.registrationsDataSources.some((persona) => persona.registrationsDataSource.count > 0)
    this.importExportLabel = hasRegistrants ? 'IMPORT / EXPORT' : 'IMPORT'

    const importTitle = this.registrationsDataSourceCollection?.registrationsDataSources[this.registrationsDataSourceCollection?.selectedIndex]?.title || ''

    // Truncate title if longer than 20 chars
    const truncatedTitle = importTitle.length > 20 ? importTitle.substring(0, 20) + '...' : importTitle

    const importOption = {
      title: 'Import - ' + truncatedTitle,
      data: { option: 1 },
      icon: { icon: 'file-import', pack: 'font-awesome' }
    }

    const exportOptions = [
      { title: 'Export to .xlsx', data: { option: 2 }, icon: { icon: 'excel', pack: 'exports' } },
      { title: 'Export to Benchmark', data: { option: 3 }, icon: { icon: 'benchmark', pack: 'exports' } }
    ]

    const selectedForm = this.registrationsDataSourceCollection?.registrationsDataSources[this.registrationsDataSourceCollection?.selectedIndex]?.registrationsDataSource?.form
    const isMemberPersona = selectedForm?.type === PersonaTypes.MEMBER
    if (isMemberPersona ? this.auth.isAdmin : this.workspaceRole.canImportToEvent()) {
      this.importExportMenu.push(importOption)
    }

    // Add export options if not hidden and allowed
    if (hasRegistrants && this.workspaceRole.canExportDatabase()) {
      this.importExportMenu.push(...exportOptions)
    }
  }
  /**
   * Search term changed event handler
   * @param value string
   */
  onSearchTermChangedEvent(value: string) {
    this.searchTermChangedSubject.next(value)
  }
  /**
   * Starts an export
   */
  export() {
    this.eventService
      .export(this.currentEvent.slug)
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this.toastrService.success('Congratulations!', 'Please wait to recieve the export in your email')
      })
  }
  /**
   * Connects to benchmark using the provided email and password
   */
  connectBenchmark() {
    this.benchmarkService
      .connectBenchmark(this.benchmarkEmail, this.benchmarkPwd)
      .pipe(takeUntil(this._destroy$))
      .subscribe((res: any) => {
        if (res.Response.Status === '1') {
          this.benchmarkService.setWithExpiry('benchmarkToken', res.Response.Token, 864000000)
          this.showSuccess = true
          this.exportBm()
        } else {
          this.show = true
        }
      })
  }

  /**
   * Starts a benchmark export
   */
  exportBm() {
    const bmToken = this.benchmarkService.getWithExpiry('benchmarkToken')
    this.benchmarkService
      .exportBenchmark(this.currentEvent.slug, bmToken)
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => console.info('start export'))
  }
  /**
   * Whether the export option is disabled
   * @returns boolean
   */
  isDisabled() {
    if (!!this.progressPercent && this.progressPercent < 100) {
      return true
    } else {
      return false
    }
  }

  /**
   * Opens an export dialog
   * @param dialog TemplateRef
   */
  openExportDialog(dialog: TemplateRef<any>) {
    this.dialogService.open(dialog, { autoFocus: false })
  }

  /**
   * Opens a settle dialog
   */
  openSettleDialog() {
    if (this.selectedSource.registrationsDataSource.allSelected) {
      this.dialogService.open(SettleRegistrantsComponent, {
        autoFocus: false,
        context: { registrantMode: false, source: this.selectedSource.registrationsDataSource, eventId: this.currentEvent.id, selectedForm: this.selectedSource.slug }
      })
    } else {
      this.dialogService.open(SettleRegistrantsComponent, {
        autoFocus: false,
        context: { registrantMode: false, source: this.selectedSource.registrationsDataSource, eventId: this.currentEvent.id }
      })
    }
  }
  /**
   * Opens any of the bulk actions dialogs
   * @param dialog TemplateRef
   */
  openBulkAction(dialog: TemplateRef<any>) {
    this.dialogService.open(dialog, { autoFocus: false })
  }

  /**
   * Opens the invitation dialog
   * @param proceedWithoutSettling boolean
   */
  openInvite(proceedWithoutSettling = false) {
    if (!this.selectionExists || this.numberOfselected() < 1) {
      this.toastrService.warning('Select at least one registration to invite.', '')
      return
    }
    if (this._inviteUnsettledCheckInFlight || this.showInviteModal) return

    const selection = this.selectedSource.registrationsDataSource
    const registrationsDataSource = this.selectedSource.registrationsDataSource
    let payload: Partial<InvitationInfo>
    if (registrationsDataSource.isChildSelected) {
      payload = {
        formSlug: registrationsDataSource.childForm.slug,
        action: registrationsDataSource.bulkOperationChildAction,
        ids: registrationsDataSource.bulkOperationChildIds,
        emails: [],
        selectedTemplate: 1,
        isInvitationModal: true,
        selectionCount: selection['_CountOperationChildRegistrants'](selection.bulkOperationChildIds),
        parent_id: registrationsDataSource.currentParentId,
        search: this.databaseService.buildBulkRegistrationsSearchQuery(registrationsDataSource.initializeRequest)
      }
    } else {
      payload = {
        formSlug: registrationsDataSource.form.slug,
        action: registrationsDataSource.bulkOperationAction,
        ids: registrationsDataSource.bulkOperationIds,
        emails: [],
        selectedTemplate: 1,
        isInvitationModal: true,
        selectionCount: selection['_CountOperationRegistrants'](selection.bulkOperationIds),
        search: this.databaseService.buildBulkRegistrationsSearchQuery(registrationsDataSource.initializeRequest)
      }
    }

    // Close settle prompt if proceeding without settling, then run unsettled check.
    if (proceedWithoutSettling) this.closeSettleToInviteModal()

    this._inviteUnsettledCheckInFlight = true
    this._inviteService
      .unsSettledCount({
        eventSlug: registrationsDataSource.initializeRequest.event,
        formSlug: payload.formSlug!,
        action: payload.action!,
        ids: payload.ids!,
        selectionCount: payload.selectionCount
      })
      .pipe(
        takeUntil(this._destroy$),
        finalize(() => {
          this._inviteUnsettledCheckInFlight = false
        })
      )
      .subscribe({
        next: (res) => {
          this.unsettledCount = res['unsettled_count']
          if (this.unsettledCount > 0 && !proceedWithoutSettling) {
            if (!this._modalService.isModalOpen(this.settleToInviteModalId)) {
              this._modalService.toggleModal(this.settleToInviteModalId)
            }
            return
          }
          this.closeSettleToInviteModal()
          this.openInvitationDialog(registrationsDataSource, selection, payload)
        },
        error: () => {
          this.toastrService.warning('Could not verify settlement status; opening invite anyway.', '')
          this.closeSettleToInviteModal()
          this.openInvitationDialog(registrationsDataSource, selection, payload)
        }
      })
  }

  private openInvitationDialog(registrationsDataSource: RegistrationsDataSource, selection: RegistrationsDataSource, payload: Partial<InvitationInfo>): void {
    this.invitationInfo = buildInvitationInfo({
      fromEventSlug: registrationsDataSource.initializeRequest.event,
      fromFormSlug: payload.formSlug ?? '',
      action: payload.action ?? '',
      ids: payload.ids ?? [],
      isInvitationModal: true,
      selectionCount: registrationsDataSource.isChildSelected
        ? selection['_CountOperationChildRegistrants'](selection.bulkOperationChildIds)
        : selection['_CountOperationRegistrants'](selection.bulkOperationIds),
      parent_id: payload.parent_id,
      search: payload.search
    })
    this.showInviteModal = true
    // Create InvitationComponent now — do not wait for a full-app CD pass over the huge DataTable.
    this._cdr.detectChanges()
  }

  /**
   * Clears invitation modal state after close.
   */
  onInviteModalClosed(): void {
    this.showInviteModal = false
    this.invitationInfo = null
  }

  /**
   * Closes the settle-to-invite modal if open.
   */
  closeSettleToInviteModal(): void {
    if (this._modalService.isModalOpen(this.settleToInviteModalId)) {
      this._modalService.toggleModal(this.settleToInviteModalId)
    }
  }

  /**
   * Opens the settle by registrations dialog
   */
  openSettle(fromSettleToInvite = false) {
    // Check if there are any selected registrants (either parent or child)
    const hasParentSelection = this.selectedSource.registrationsDataSource.selectionStatus !== 'None'
    const hasChildSelection = this.selectedSource.registrationsDataSource.childSelectionStatus !== 'None'

    if (!hasParentSelection && !hasChildSelection) {
      this.toastrService.error('Error', 'You must select at least one registrant to settle')
      return
    }
    this.selectedSource.registrationsDataSource.allSelected
      ? this.openSettleDialog()
      : this.dialogService
          .open(SettleRegistrantsComponent, {
            autoFocus: false,
            context: {
              registrantMode: true,
              source: this.selectedSource.registrationsDataSource
            }
          })
          .onClose.pipe(takeUntil(this._destroy$))
          .subscribe(() => this.settleComplete.emit())
    if (fromSettleToInvite) this.closeSettleToInviteModal()
  }
  /**
   * Toggles AutoRefresh and emits the event
   * @param value boolean
   */
  toggleAutoRefresh(value: boolean) {
    this.autoRefresh = value
    this.autoRefreshChanged.next(this.autoRefresh)
  }

  /**
   * Updates the status options to mark the selected one
   */
  private _updateStatusOptionsSelected(): void {
    this.statusOptions = this.statusOptions.map((option) => ({
      ...option,
      selected: option.data.option === this._selectedStatusOption
    }))
  }

  /**
   * Opens a dialog and handles it's unsubscription
   * @param dialog Template ref
   */
  openDialog(dialog: TemplateRef<any>) {
    const dialogClose$ = this.dialogService
      .open(dialog, { autoFocus: false })
      .onClose.pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        dialogClose$.unsubscribe()
      })
  }

  /**
   * Downloads the csv upload template for the selected persona
   */
  downloadTemplate() {
    const formIndex = this.registrationsDataSourceCollection.selectedIndex
    const form = this.registrationsDataSourceCollection.registrationsDataSources[formIndex]
    window.open(`${environment.registrationsApi}import/${this.auth.currentWorkspace.slug}/${this.currentEvent.slug}/${form.slug}`, '_blank')
  }

  /**
   * File change event handler
   * @param event
   */
  onFileChange(event) {
    if (event.target.files.length > 0) {
      const file = event.target.files[0]
      this.file.patchValue(file)
    }
  }

  /**
   * Uploads the file of registrants to be imported
   */
  upload() {
    const formIndex = this.registrationsDataSourceCollection.selectedIndex
    const formSlug = this.registrationsDataSourceCollection.registrationsDataSources[formIndex].slug
    this.databaseService
      .uploadImportFile(this.currentEvent.slug, formSlug, { file: this.file.value, send_email: this.confirmationEmail.value })
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this.toastrService.success('Importing your data ...', 'You will receive an email with the results shortly')
      })
  }
  /**
   * Opens channel + cost flow for bulk resend confirmation
   */
  openResendConfirmationFlow() {
    if (this.numberOfselected() < 1) {
      this.toastrService.warning('', 'Select at least one registrant to resend confirmation.')
      return
    }
    const selection = this.selectedSource.registrationsDataSource
    if (selection.isChildSelected) {
      if (selection.bulkOperationChildRegistrants.some((reg) => [ApprovalStatus.PENDING_REVIEW, ApprovalStatus.AWAITING_REVISION].includes(reg.entry_status))) {
        this.toastrService.error('', 'You can only resend confirmation emails to registrants that not rejected, awaiting payment or awaiting revision')
        return
      }
      this.dialogService.open(ResendConfirmationFlowComponent, {
        context: {
          mode: 'bulk',
          attendeeCount: this.numberOfselected(),
          eventSlug: this.currentEvent.slug,
          isChildSelected: true,
          bulkAction: selection.bulkOperationChildAction,
          bulkPersonaSlug: selection.childForm?.slug ?? null,
          bulkIds: selection.bulkOperationChildIds,
          bulkCount: selection['_CountOperationChildRegistrants'](selection.bulkOperationChildIds),
          parentId: selection.currentParentId,
          search: this.databaseService.buildBulkRegistrationsSearchQuery(selection.initializeRequest)
        },
        autoFocus: false,
        closeOnBackdropClick: true,
        closeOnEsc: true
      })
      return
    }

    if (selection.bulkOperationRegistrants.some((reg) => [ApprovalStatus.PENDING_REVIEW, ApprovalStatus.AWAITING_REVISION].includes(reg.entry_status))) {
      this.toastrService.error('', 'You can only resend confirmation emails to registrants that not rejected, awaiting payment or awaiting revision')
      return
    }

    this.dialogService.open(ResendConfirmationFlowComponent, {
      context: {
        mode: 'bulk',
        attendeeCount: this.numberOfselected(),
        eventSlug: this.currentEvent.slug,
        isChildSelected: false,
        bulkAction: selection.bulkOperationAction,
        bulkPersonaSlug: selection.form.slug,
        bulkIds: selection.bulkOperationIds,
        bulkCount: selection['_CountOperationRegistrants'](selection.bulkOperationIds),
        search: this.databaseService.buildBulkRegistrationsSearchQuery(selection.initializeRequest)
      },
      autoFocus: false,
      closeOnBackdropClick: true,
      closeOnEsc: true
    })
  }

  /**
   * Checks if the current form is a group form
   */
  isGroupForm(): boolean {
    const dataSource = this.selectedSource.registrationsDataSource
    return dataSource.form.type === PersonaTypes.GROUP
  }

  totalChildrenCount(): number {
    const dataSource = this.selectedSource.registrationsDataSource

    // get the total count from the child form datasource
    if (dataSource.allSelected && dataSource.selectionStatus === 'All') {
      const childForm = dataSource.form.subForm
      if (childForm) {
        const childDataSource = this.registrationsDataSourceCollection.registrationsDataSources.find((ds) => ds.registrationsDataSource.form.id === childForm.id)
        if (childDataSource) return childDataSource.registrationsDataSource.count
      }
    }

    // For partial selections, count children from selected parents
    return (dataSource.bulkOperationRegistrants || []).reduce((sum, parent: any) => sum + (parent.child_count || 0), 0)
  }
  /**
   * Returns the number of selected registrants
   * - Individual forms: returns selection count
   * - Group forms: returns parent count + their children count, or just children if expanded
   */
  numberOfselected() {
    const dataSource = this.selectedSource.registrationsDataSource

    // Non-group forms
    if (!this.isGroupForm()) {
      return dataSource.selectionCount
    }

    // Get the datasource reference
    const datasource = this.selectedSource.registrationsDataSource

    // If children are directly selected (not through parent selection), return child count
    if (datasource.isChildSelected || datasource.childSelectionStatus !== 'None') {
      return datasource.childSelectionCount
    }

    // Count parents
    const parentCount = datasource.selectionCount

    // Get selected parents from the data
    const selectedParents = datasource.selectedRegistrants
    const allParents = datasource.data

    // If no parents selected, return just parent count
    if (!selectedParents || selectedParents.length === 0) {
      return parentCount
    }

    // When parents are selected, we need to calculate the total count of parents and their children
    // If all parents are selected (allSelected is true), we need to count children for all visible parents
    let childrenCount = 0

    if (datasource.allSelected && datasource.selectionStatus === 'All') {
      // All selected - count children for all visible parents
      allParents.forEach((registration) => {
        if (registration.child_count && registration.child_count > 0) {
          childrenCount += registration.child_count
        }
      })
    } else {
      // Some or none - count children only for selected parents
      selectedParents.forEach((registration) => {
        if (registration.child_count && registration.child_count > 0) {
          childrenCount += registration.child_count
        }
      })
    }

    // Return total count (parents + children)
    return parentCount + childrenCount
  }

  /**
   * Returns the selection tooltip text based on form type
   */
  getSelectionTooltip(): string {
    if (!this.isGroupForm()) {
      return 'Selected count'
    }

    const dataSource = this.selectedSource.registrationsDataSource

    // Group form - children selected
    if (dataSource.childSelectionStatus !== 'None') {
      return `Selected members: ${dataSource.childSelectionCount}`
    }

    // Group form - parents selected
    const groupCount = dataSource.selectionCount
    return `Selected: ${groupCount} Group${groupCount > 1 ? 's' : ''} and ${this.totalChildrenCount()} Member${this.totalChildrenCount() > 1 ? 's' : ''}`
  }

  /**
   * Returns the selection display text
   */
  getSelectionDisplay(): string {
    if (!this.isGroupForm()) {
      return `${this.numberOfselected()}`
    }

    const dataSource = this.selectedSource.registrationsDataSource

    // Group form - children selected
    if (dataSource.childSelectionStatus !== 'None') {
      return ` ${dataSource.childSelectionCount} `
    }

    // Group form - parents selected
    const groupCount = dataSource.selectionCount
    return `${groupCount} Group${groupCount > 1 ? 's' : ''}, ${this.totalChildrenCount()} Member${this.totalChildrenCount() > 1 ? 's' : ''}`
  }

  /**
   * Toggles the visability of a column
   * @param event Event
   * @param index Column index
   * @returns void
   */
  public toggleVisability(event: Event, index: number): void {
    event.stopPropagation()
    this.visabilityOptions[this.registrationsDataSourceCollection.selectedIndex][index].data.checked =
      !this.visabilityOptions[this.registrationsDataSourceCollection.selectedIndex][index].data.checked
    this.toggleColumns()
  }

  /**
   * Reorders the columns of datatable
   * @returns void
   */
  private toggleColumns(): void {
    const columns = this.visabilityOptions[this.registrationsDataSourceCollection.selectedIndex].filter((option) => option.data.checked).map((option) => option.data.option)
    this.selectedSource.registrationsDataSource.displayColumns = columns
  }

  /**
   * Deletes all selected registrants
   * @returns void
   */
  deleteSelections(ref: any): void {
    if (+this.confirmationNumber !== this.numberOfselected()) {
      this.confirmationText = 'The number you entered is incorrect'
      return
    }
    this.confirmationText = ''
    this.confirmationNumber = ''
    const isChildDeletion = this.selectedSource.registrationsDataSource.isChildSelected
    const deletedCount = this.numberOfselected()
    this.registrationsDataSourceCollection.deleteSelections()
    ref.close()

    // Update child form datasource count if deleting child members
    if (isChildDeletion) {
      this.selectedSource.registrationsDataSource.changed$.pipe(takeUntil(this._destroy$)).subscribe(() => {
        const childFormId = this.selectedSource.registrationsDataSource.childForm?.id
        if (childFormId) {
          // Find the child form datasource in the collection and update its count
          const childDataSource = this.registrationsDataSourceCollection.registrationsDataSources.find((ds) => ds.registrationsDataSource.form.id === childFormId)
          if (childDataSource) childDataSource.registrationsDataSource.count -= deletedCount
        }

        // Clear child selection state
        this.selectedSource.registrationsDataSource.childAllSelected = false

        // Trigger change detection to update persona switcher counts and child table UI
        if (this.registrationsDataSourceCollection.changed$) this.registrationsDataSourceCollection.changed$.next(true)
      })
    } else {
      // For parent deletions, just trigger change detection
      if (this.registrationsDataSourceCollection.changed$) this.registrationsDataSourceCollection.changed$.next(true)
    }
  }

  /**
   * Opens the bulk revision request modal
   */
  openBulkRevisionRequestModal() {
    const selection = this.selectedSource.registrationsDataSource

    // Extract only the needed fields from each registrant based on selection type
    let dataToBeSentToModal
    if (selection.isChildSelected) {
      dataToBeSentToModal = selection.bulkOperationChildRegistrants.map((reg) => ({
        fields: {
          fullname: reg.fields.fullname,
          email: reg.fields.email
        }
      }))
    } else {
      dataToBeSentToModal = selection.bulkOperationRegistrants.map((reg) => ({
        fields: {
          fullname: reg.fields.fullname,
          email: reg.fields.email
        }
      }))
    }

    this.dialogService
      .open(RevisionRequestModalComponent, {
        context: {
          registrants: dataToBeSentToModal,
          event: this.currentEvent,
          isBulkMode: true,
          count: this.numberOfselected()
        },
        autoFocus: false
      })
      .onClose.pipe(first(), takeUntil(this._destroy$))
      .subscribe((res) => {
        if (res?.subject || res?.message) {
          this.databaseService.email = {
            subject: res.subject,
            message: res.message,
            registrationButtonContent: res.registrationButtonContent || 'Edit Registration'
          }
          this.registrationsDataSourceCollection.requestRevisionSelections()
        }
      })
  }

  /**
   * Opens the dialog to import registrations.
   * Member personas are limited to platform admins.
   */
  openImportDialog() {
    const selectedForm = this.registrationsDataSourceCollection?.registrationsDataSources[this.registrationsDataSourceCollection?.selectedIndex]?.registrationsDataSource?.form
    if (selectedForm?.type === PersonaTypes.MEMBER && !this.auth.isAdmin) {
      this.toastrService.error('', 'Only platform admins can import members onto a group parent')
      return
    }

    // Create a dedicated dialog ref for the ImportRegistrationsComponent with proper config
    const importDialog = this.dialogService.open(ImportRegistrationsComponent, {
      context: {
        registrationsDataSourceCollection: this.registrationsDataSourceCollection
      },
      hasBackdrop: true,
      closeOnBackdropClick: true,
      closeOnEsc: true,
      hasScroll: false,
      autoFocus: false
    })

    importDialog.onClose.pipe(first(), takeUntil(this._destroy$)).subscribe(() => {
      this.settleComplete.emit()
    })

    // Set up component instance with needed data
    const component = importDialog.componentRef.instance
    component.dialogRef = importDialog

    // Handle component events
    component.downloadTemplateEvent.pipe(takeUntil(this._destroy$)).subscribe(() => this.downloadTemplate())
    component.fileChangeEvent.pipe(takeUntil(this._destroy$)).subscribe((file: File) => {
      if (file) {
        const fakeEvent = { target: { files: [file] } }
        this.onFileChange(fakeEvent)
      }
    })
    component.uploadEvent.pipe(takeUntil(this._destroy$)).subscribe((data) => {
      // Use the data from event instead of just calling this.upload()
      const formIndex = this.registrationsDataSourceCollection.selectedIndex
      const formSlug = this.registrationsDataSourceCollection.registrationsDataSources[formIndex].slug

      // Pass the data from import component to the upload method
      this.databaseService
        .uploadImportFile(this.currentEvent.slug, formSlug, {
          file: this.file.value,
          send_email: data?.sendConfirmationEmail || false
        })
        .pipe(takeUntil(this._destroy$))
        .subscribe(() => {
          this.toastrService.success('Importing your data ...', 'You will receive an email with the results shortly')

          // No need to close the dialog here as it's already closed by the component
        })
    })

    return importDialog
  }

  /**
   * Get the count of selected parents
   */
  getParentCount() {
    const datasource = this.selectedSource.registrationsDataSource
    // If children are directly selected, no parents are selected
    if (datasource.isChildSelected || datasource.childSelectionStatus !== 'None') {
      return 0
    }
    return datasource.selectionCount
  }

  /**
   * Get the count of children belonging to selected parents or directly selected children
   */
  getChildrenCount() {
    const datasource = this.selectedSource.registrationsDataSource

    // If children are directly selected, return the count of selected children
    if (datasource.isChildSelected || datasource.childSelectionStatus !== 'None') {
      return datasource.childSelectionCount
    }

    const selectedParents = datasource.selectedRegistrants
    const allParents = datasource.data

    if (!selectedParents || selectedParents.length === 0) {
      return 0
    }

    let childrenCount = 0

    if (datasource.allSelected && datasource.selectionStatus === 'All') {
      allParents.forEach((registration) => {
        if (registration.child_count && registration.child_count > 0) {
          childrenCount += registration.child_count
        }
      })
    } else {
      selectedParents.forEach((registration) => {
        if (registration.child_count && registration.child_count > 0) {
          childrenCount += registration.child_count
        }
      })
    }

    return childrenCount
  }

  // Add properties to track selected index
  private lastSelectedIndex = -1
  private selectedIndexChangeInterval: any

  /**
   * Remove a specific filter by field name
   */
  removeFilter(field: string): void {
    this.filterRemoved.emit(field)
  }

  /**
   * Clear all filters including search term
   */
  openInvitationHistoryDialog(): void {
    this.dialogService.open(InvitationJobsHistoryComponent, {
      context: { currentEvent: this.currentEvent },
      hasBackdrop: true,
      closeOnBackdropClick: true,
      closeOnEsc: true,
      autoFocus: false
    })
  }

  clearAllFilters(): void {
    this.searchTerm = ''
    this.onSearchTermChangedEvent('')
    this.allFiltersCleared.emit()
  }

  /**
   * Handle save filter request from dropdown
   */
  onSaveFilter(filterName: string): void {
    this.saveFilterRequested.emit(filterName)
  }

  /**
   * Handle load filter request from dropdown
   */
  onLoadFilter(filter: SavedFilter): void {
    this.loadFilterRequested.emit(filter)
  }

  /**
   * Handle delete filter request from dropdown
   */
  onDeleteFilter(filterId: string): void {
    this.deleteFilterRequested.emit(filterId)
  }

  /**
   * Check if there are active filters (for showing save button)
   */
  get hasActiveFilters(): boolean {
    return this.activeFilters.length > 0 || !!this.searchTerm
  }

  /**
   * Opens the AI search modal
   */
  openAISearch(): void {
    const currentForm = this.selectedSource?.registrationsDataSource?.form
    const eventDays = this.currentEvent?.event_timings?.map((t) => t.start_datetime?.split('T')[0]) || []

    const dialog = this.dialogService.open(AiSearchModalComponent, {
      context: {
        form: currentForm,
        eventDays: eventDays
      },
      autoFocus: false,
      hasBackdrop: true,
      closeOnBackdropClick: true,
      closeOnEsc: true
    })

    dialog.onClose.pipe(first(), takeUntil(this._destroy$)).subscribe((filters: AISearchResponse['filters']) => {
      if (filters) {
        // Apply the search term if present
        if (filters.searchTerm) {
          this.searchTerm = filters.searchTerm
          this.searchTermChangedSubject.next(this.searchTerm)
        }
        // Emit the AI-generated filters
        this.aiFiltersGenerated.emit(filters)
        this.toastrService.success('AI filters applied', 'Your search has been converted to filters')
      }
    })
  }
}
